import find from 'lodash/find';
import get from 'lodash/get';
import some from 'lodash/some';
import values from 'lodash/values';

export const SUPPLIERS_TAB_ID = 'suppliers';
export const CURRENCY_TAB_ID = 'currency';
export const SHIPMENT_COUNTRY_TAB_ID = 'shipmentCountry';
export const SHIPPING_TIME_TAB_ID = 'shippingTime';
export const SUPPLIER_ALPHABET_FILTERS = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z',
  '0-9',
  'Other',
];

export const TAB_CONFIGS = [
  {
    id: SUPPLIERS_TAB_ID,
    label: 'Commercial suppliers',
    heading: 'Choose preferred suppliers',
    searchPlaceholder: 'Type supplier name',
  },
  {
    id: CURRENCY_TAB_ID,
    label: 'Currency',
    heading: 'Select accepted currencies',
    searchPlaceholder: 'Type Currency',
  },
  {
    id: SHIPMENT_COUNTRY_TAB_ID,
    label: 'Shipment country',
    heading: 'Select shipment country',
    searchPlaceholder: 'Type country name',
  },
  {
    id: SHIPPING_TIME_TAB_ID,
    label: 'Shipping time',
    heading: 'Select shipping time',
  },
];

export const INITIAL_SELECTIONS = {
  [SUPPLIERS_TAB_ID]: [],
  [CURRENCY_TAB_ID]: [],
  [SHIPMENT_COUNTRY_TAB_ID]: [],
  [SHIPPING_TIME_TAB_ID]: [],
};
export const PREFERENCE_CONFIGS = {
  [SUPPLIERS_TAB_ID]: {
    groupKey: 'SUPPLIERS',
    preferenceKey: 'RX2_SUPPLIERS',
    filterId: 'SUPP.PREF_SNAME',
    resultFilterId: 'SUPP.SNAME',
  },
  [CURRENCY_TAB_ID]: {
    groupKey: 'CURRENCIES',
    preferenceKey: 'RX2_CURRENCIES',
    filterId: 'PROD.PREF_CURRENCY',
    resultFilterId: 'PROD.CURRENCY',
  },
  [SHIPMENT_COUNTRY_TAB_ID]: {
    groupKey: 'SHIPMENT_COUNTRIES',
    preferenceKey: 'RX2_SHIPMENT_COUNTRY',
    filterId: 'PROD.PREF_LOC',
    resultFilterId: 'PROD.LOC',
  },
  [SHIPPING_TIME_TAB_ID]: {
    groupKey: 'SHIPPING_TIMES',
    preferenceKey: 'RX2_SHIPPING_TIME',
    filterId: 'PROD.PREF_TIER',
    resultFilterId: 'PROD.TIER',
  },
};

export const COMMERCIAL_SUBSTANCES_PREFERENCES_SECTION = 'PREFERRED_SUPPLIERS';

export function normalizeText(value = '') {
  const normalizedValue = `${value}`;
  const trimmedValue = normalizedValue.trim();
  return trimmedValue.toLowerCase();
}

export function getSafeDomId(value = '') {
  const normalizedValue = normalizeText(value);
  return normalizedValue.replace(/[^a-z0-9_-]+/g, '-');
}

export function getTabButtonId(tabId, idPrefix = 'commercial-substances-preferences') {
  return `${idPrefix}-tab-${getSafeDomId(tabId)}`;
}

export function getTabLabel(tab, selections) {
  const selectionCount = (selections[tab.id] || []).length;
  return selectionCount > 0 ? `${tab.label} (${selectionCount})` : tab.label;
}

export function getTabConfig(tabId) {
  return find(TAB_CONFIGS, { id: tabId }) || TAB_CONFIGS[0];
}

export function splitIntoColumns(options) {
  const midpoint = Math.ceil(options.length / 2);
  return [options.slice(0, midpoint), options.slice(midpoint)];
}

export function filterOptionsBySearch(options, searchValue) {
  const normalizedSearch = normalizeText(searchValue);

  if (!normalizedSearch) {
    return options;
  }

  return options.filter((option) => {
    const normalizedOptionLabel = normalizeText(option.label);
    return normalizedOptionLabel.includes(normalizedSearch);
  });
}

export function getSupplierAlphabetFilter(option = {}) {
  const firstCharacter = normalizeText(option.label || option.name).charAt(0);

  if (/^[a-z]$/.test(firstCharacter)) {
    return firstCharacter.toUpperCase();
  }

  if (/^\d$/.test(firstCharacter)) {
    return '0-9';
  }

  return 'Other';
}

export function hasSelectedPreferences(selections) {
  return some(values(selections), (selectedValues) => (selectedValues || []).length > 0);
}

export function getPreferenceConfig(tabId) {
  return PREFERENCE_CONFIGS[tabId];
}

export function getPreferencePath(tabId) {
  const config = getPreferenceConfig(tabId);
  return [config.groupKey, config.preferenceKey];
}

export function getPreferenceOptionsMap(model, tabId) {
  const config = getPreferenceConfig(tabId);
  return get(model, [config.groupKey, 'preferences', config.preferenceKey, 'preferences'], {});
}

function getPreferenceOptionName(optionsMapEntry, optionKey, tabId) {
  const option = optionsMapEntry || {};

  if (tabId !== SUPPLIERS_TAB_ID && option.label && normalizeText(option.name) === normalizeText(optionKey)) {
    return option.label;
  }

  if (option.name) {
    return option.name;
  }

  if (tabId !== SUPPLIERS_TAB_ID && option.label) {
    return option.label;
  }

  return optionKey;
}

export function preferenceOptionsMapToList(tabId, optionsMap = {}) {
  return Object.keys(optionsMap).map((name) => ({
    id: name,
    name: getPreferenceOptionName(optionsMap[name], name, tabId),
    label: optionsMap[name]?.label || name,
  }));
}

export function getPreferenceOptions(model, tabId) {
  return preferenceOptionsMapToList(tabId, getPreferenceOptionsMap(model, tabId));
}

export function normalizePreferenceValue(value) {
  return normalizeText(value).replace(/[^a-z0-9]+/g, '');
}

function getPreferenceValueFromMap(preferencesMap = {}, option = {}) {
  const candidateNames = [option.id, option.name, option.label].filter(Boolean);
  const exactCandidate = candidateNames.find((candidateName) => Object.prototype.hasOwnProperty.call(preferencesMap, candidateName));

  if (exactCandidate) {
    return preferencesMap[exactCandidate];
  }

  const normalizedCandidates = candidateNames.map(normalizePreferenceValue);
  const matchingPreferenceName = Object.keys(preferencesMap).find((preferenceName) =>
    normalizedCandidates.includes(normalizePreferenceValue(preferenceName))
  );

  return matchingPreferenceName ? preferencesMap[matchingPreferenceName] : false;
}

function getPreferencePayloadName(value, option = {}, preferencesMap = {}) {
  const candidateNames = [value, option.id, option.name, option.label].filter(Boolean);
  const exactCandidate = candidateNames.find((candidateName) => Object.prototype.hasOwnProperty.call(preferencesMap, candidateName));

  if (exactCandidate) {
    return exactCandidate;
  }

  if (option.id) {
    return option.id;
  }

  const normalizedCandidates = candidateNames.map(normalizePreferenceValue);
  const matchingPreferenceName = Object.keys(preferencesMap).find((preferenceName) =>
    normalizedCandidates.includes(normalizePreferenceValue(preferenceName))
  );

  return matchingPreferenceName || option.name || option.label || value;
}

function dedupePreferencePayloadNames(payloadNames = []) {
  const seenNames = new Set();

  return payloadNames.filter((payloadName) => {
    const normalizedPayloadName = normalizePreferenceValue(payloadName);

    if (seenNames.has(normalizedPayloadName)) {
      return false;
    }

    seenNames.add(normalizedPayloadName);
    return true;
  });
}

function doesPreferenceOptionMatchValue(value, option = {}) {
  const normalizedValue = normalizePreferenceValue(value);

  return [option.id, option.name, option.label]
    .filter(Boolean)
    .some((optionValue) => normalizePreferenceValue(optionValue) === normalizedValue);
}

export function getPersistablePreferenceSelections(optionsByTab = {}, selections = INITIAL_SELECTIONS) {
  return TAB_CONFIGS.reduce((accumulator, tab) => {
    const options = optionsByTab[tab.id] || [];
    const selectedValues = selections[tab.id] || [];

    return {
      ...accumulator,
      [tab.id]: selectedValues.filter((value) => options.some((option) => doesPreferenceOptionMatchValue(value, option))),
    };
  }, {});
}

function getSelectedPreferencePayloadNames(selectedValues = [], optionsById = {}, preferencesMap = {}) {
  return selectedValues.map((value) => getPreferencePayloadName(value, optionsById[value], preferencesMap));
}

function hasPersistablePreferenceOption(value, options = []) {
  return options.some((option) => doesPreferenceOptionMatchValue(value, option));
}

function isPayloadNameSelected(payloadName, selectedPayloadNames = []) {
  const normalizedPayloadName = normalizePreferenceValue(payloadName);

  return selectedPayloadNames.some((selectedPayloadName) => normalizePreferenceValue(selectedPayloadName) === normalizedPayloadName);
}

export function getSelectedPreferenceIds(model, tabId, options) {
  const preferencePath = getPreferencePath(tabId);
  const preferencesMap = get(model, preferencePath, {});

  return options.filter((option) => getPreferenceValueFromMap(preferencesMap, option)).map((option) => option.id);
}

export function getPreferenceSelections(model, optionsByTab) {
  return TAB_CONFIGS.reduce(
    (accumulator, tab) => ({
      ...accumulator,
      [tab.id]: getSelectedPreferenceIds(model, tab.id, optionsByTab[tab.id] || []),
    }),
    {}
  );
}

export function getPreferenceOptionsByTab(model) {
  return TAB_CONFIGS.reduce(
    (accumulator, tab) => ({
      ...accumulator,
      [tab.id]: getPreferenceOptions(model, tab.id),
    }),
    {}
  );
}

export function getPreferencePayload(optionsByTab, selections, previousSelections, previousModel, includedTabIds) {
  const effectivePreviousSelections = previousSelections || INITIAL_SELECTIONS;
  const effectivePreviousModel = previousModel || {};
  let includedTabIdsSet = null;

  if (includedTabIds) {
    const normalizedIncludedTabIds = Array.isArray(includedTabIds) ? includedTabIds : [includedTabIds];
    includedTabIdsSet = new Set(normalizedIncludedTabIds);
  }

  return TAB_CONFIGS.reduce((payload, tab) => {
    if (includedTabIdsSet && !includedTabIdsSet.has(tab.id)) {
      return payload;
    }

    const selectedValues = selections[tab.id] || [];
    const previousSelectedValues = effectivePreviousSelections[tab.id] || [];
    const options = optionsByTab[tab.id] || [];

    if (options.length === 0) {
      return payload;
    }

    const preferencesMap = get(effectivePreviousModel, getPreferencePath(tab.id), {});
    const optionsById = options.reduce((accumulator, option) => {
      [option.id, option.name, option.label].filter(Boolean).forEach((optionKey) => {
        accumulator[optionKey] = option;
      });
      return accumulator;
    }, {});
    const selectedPayloadNames = getSelectedPreferencePayloadNames(selectedValues, optionsById, preferencesMap);
    const previouslySavedPayloadNames = [
      ...previousSelectedValues.map((value) => getPreferencePayloadName(value, optionsById[value], preferencesMap)),
      ...Object.keys(preferencesMap)
        .filter((name) => preferencesMap[name] && hasPersistablePreferenceOption(name, options))
        .map((name) => getPreferencePayloadName(name, optionsById[name], preferencesMap)),
    ];
    const candidateNames = dedupePreferencePayloadNames([...selectedPayloadNames, ...previouslySavedPayloadNames]);

    if (tab.id === SUPPLIERS_TAB_ID || includedTabIdsSet) {
      const tabPreferences = candidateNames.map((name) => ({
        name,
        value: isPayloadNameSelected(name, selectedPayloadNames),
      }));

      return payload.concat(tabPreferences);
    }

    const changedPreferences = candidateNames.reduce((changes, name) => {
      const isSelected = isPayloadNameSelected(name, selectedPayloadNames);
      const wasSelected = isPayloadNameSelected(name, previouslySavedPayloadNames);

      if (isSelected !== wasSelected) {
        changes.push({ name, value: isSelected });
      }

      return changes;
    }, []);

    return payload.concat(changedPreferences);
  }, []);
}
